import React, { useEffect, useState } from "react";
import { Plus, Search, BookOpen, Trash2, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

function LedgerModal({ open, onClose, onSaved, groups }) {
  const [f, setF] = useState({ name: "", group_id: "", opening_balance: 0, opening_side: "dr" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: "", group_id: groups[0]?.id || "", opening_balance: 0, opening_side: "dr" }); }, [open, groups]);
  if (!open) return null;
  const submit = async () => {
    if (!f.name || !f.group_id) { toast.error("Name and group required"); return; }
    setSaving(true);
    try {
      await api.post("/ledgers", f);
      toast.success("Ledger created");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ledger-modal">
      <div className="bg-white rounded-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-bold">New Ledger</h2>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><label className="label-field">Name *</label>
            <input data-testid="ledger-name" className="input-clean" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/></div>
          <div className="md:col-span-2"><label className="label-field">Under Group *</label>
            <select data-testid="ledger-group" className="input-clean" value={f.group_id} onChange={(e) => setF({ ...f, group_id: e.target.value })}>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.nature}</option>)}
            </select></div>
          <div><label className="label-field">Opening Balance</label>
            <input type="number" step="0.01" className="input-clean font-mono-data" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: parseFloat(e.target.value) || 0 })}/></div>
          <div><label className="label-field">Opening Side</label>
            <select className="input-clean" value={f.opening_side} onChange={(e) => setF({ ...f, opening_side: e.target.value })}>
              <option value="dr">Debit (Dr)</option><option value="cr">Credit (Cr)</option>
            </select></div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="ledger-save-btn" onClick={submit} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            {saving ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Ledgers() {
  const { active } = useCompany();
  const [ledgers, setLedgers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState(null);
  const [initing, setIniting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("india_ca");

  const load = async () => {
    if (!active) return;
    const [l, g, t] = await Promise.all([
      api.get("/ledgers"), api.get("/ledger-groups"), api.get("/coa/templates"),
    ]);
    setLedgers(l.data.ledgers || []);
    setGroups(g.data.groups || []);
    setTemplates(t.data.templates || []);
  };
  useEffect(() => { load(); }, [active]);

  const initCoA = async () => {
    setIniting(true);
    try {
      const { data } = await api.post("/coa/init", { template: selectedTpl });
      toast.success(`CoA initialized: ${data.groups_total} groups, ${data.ledgers_created} new ledgers`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setIniting(false); }
  };

  const openStatement = async (l) => {
    try {
      const { data } = await api.get(`/ledgers/${l.id}/statement`);
      setStatement(data);
    } catch { toast.error("Failed to load"); }
  };

  const remove = async (l) => {
    if (l.is_system) { toast.error("Cannot delete system ledger"); return; }
    if (!window.confirm(`Delete ledger "${l.name}"?`)) return;
    try { await api.delete(`/ledgers/${l.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = ledgers.filter((l) => !q || l.name.toLowerCase().includes(q.toLowerCase()));
  const noCoA = ledgers.length === 0;
  const cur = active?.base_currency || "INR";

  if (noCoA) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="coa-init-panel">
        <div className="card-surface p-8 text-center">
          <BookOpen size={48} className="mx-auto text-[#1E3A8A] mb-4"/>
          <h1 className="text-2xl font-bold mb-2">Set up your Chart of Accounts</h1>
          <p className="text-sm text-[#64748B] mb-6">
            Pick a template — we'll seed 28 standard Tally groups + preset ledgers so you can start posting vouchers immediately.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {templates.map((t) => (
              <button key={t.key}
                data-testid={`coa-template-${t.key}`}
                onClick={() => setSelectedTpl(t.key)}
                className={`text-left p-4 border rounded-md hover:border-[#1E3A8A] ${selectedTpl === t.key ? "border-[#1E3A8A] bg-[#DBEAFE]" : "border-[#E2E8F0]"}`}>
                <div className="font-semibold text-sm text-[#0F172A]">{t.label}</div>
                <div className="text-[11px] text-[#64748B] mt-1">{t.description}</div>
                <div className="text-[10px] font-mono-data text-[#1E3A8A] mt-2 uppercase tracking-widest">
                  +{t.extra_count} extras
                </div>
              </button>
            ))}
          </div>
          <button data-testid="coa-init-btn" onClick={initCoA} disabled={initing} className="btn-primary">
            {initing ? "Initializing…" : "Initialize Chart of Accounts"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ledgers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Ledgers</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Chart of Accounts</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-ledger-btn" className="btn-primary w-full sm:w-auto justify-center">
          <Plus size={14}/> New Ledger
        </button>
      </div>

      <div className="card-surface p-4">
        <div className="flex items-center gap-2 mb-4">
          <Search size={16} className="text-[#94A3B8]"/>
          <input className="input-clean" placeholder="Search ledger name" value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>
        <table className="table-clean">
          <thead>
            <tr><th>Name</th><th>Group</th><th>Nature</th><th className="text-right">Opening</th><th>Side</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} data-testid={`ledger-row-${l.id}`} className="cursor-pointer" onClick={() => openStatement(l)}>
                <td className="font-medium">
                  {l.name}
                  {l.is_system && <span className="chip chip-neutral ml-2 text-[10px]">system</span>}
                </td>
                <td>{l.group_name}</td>
                <td className="text-xs text-[#64748B]">{groups.find((g) => g.id === l.group_id)?.nature}</td>
                <td className="text-right font-mono-data">{fmtMoney(l.opening_balance, cur)}</td>
                <td className="text-xs uppercase font-mono-data">{l.opening_side}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <ChevronRight size={14} className="text-[#94A3B8]"/>
                    {!l.is_system && (
                      <button onClick={(e) => { e.stopPropagation(); remove(l); }} className="text-[#DC2626]">
                        <Trash2 size={12}/>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LedgerModal open={open} onClose={() => setOpen(false)} onSaved={load} groups={groups}/>

      {statement && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setStatement(null)} data-testid="ledger-statement-modal">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline">Ledger Statement</div>
                <h2 className="text-xl font-bold">{statement.ledger.name}</h2>
                <div className="text-xs text-[#64748B]">{statement.ledger.group_name}</div>
              </div>
              <button onClick={() => setStatement(null)}><X size={18}/></button>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="kpi-tile"><div className="text-xs text-[#64748B]">Opening</div>
                <div className="text-lg font-bold font-mono-data">{fmtMoney(statement.opening.amount, cur)} <span className="text-xs text-[#64748B] uppercase">{statement.opening.side}</span></div></div>
              <div className="kpi-tile"><div className="text-xs text-[#64748B]">Total Dr / Cr</div>
                <div className="text-lg font-bold font-mono-data">{fmtMoney(statement.totals.dr, cur)} / {fmtMoney(statement.totals.cr, cur)}</div></div>
              <div className="kpi-tile"><div className="text-xs text-[#64748B]">Closing</div>
                <div className="text-lg font-bold font-mono-data">{fmtMoney(statement.closing.amount, cur)} <span className="text-xs uppercase">{statement.closing.side}</span></div></div>
            </div>
            <table className="table-clean">
              <thead><tr><th>Date</th><th>Voucher</th><th>Narration</th><th className="text-right">Dr</th><th className="text-right">Cr</th><th className="text-right">Running</th></tr></thead>
              <tbody>
                {statement.lines.map((ln, i) => (
                  <tr key={i} data-testid={`statement-line-${i}`}>
                    <td className="font-mono-data text-xs">{ln.date}</td>
                    <td className="font-mono-data text-xs text-[#1E3A8A]">{ln.voucher_number}</td>
                    <td className="text-xs">{ln.narration}</td>
                    <td className="text-right font-mono-data">{ln.dr ? fmtMoney(ln.dr, cur) : "—"}</td>
                    <td className="text-right font-mono-data">{ln.cr ? fmtMoney(ln.cr, cur) : "—"}</td>
                    <td className="text-right font-mono-data font-semibold">{fmtMoney(ln.running, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
